/**
 * ============================================================================
 * LLM 프롬프트 템플릿 및 서비스 정보
 *
 * 레거시 js/llm-validation.js에서 1:1 포팅
 *
 * 구성:
 * - LLMServiceType: LLM 서비스 타입 정의
 * - LLMServiceInfo: LLM 서비스 상세 정보 인터페이스
 * - LLM_SERVICES: 5개 LLM 서비스 정보 (특성 + URL 통합)
 * - LLM_PROMPTS: 5개 LLM별 프롬프트 템플릿
 *
 * @version 1.0.0
 * @since 2026-03-03
 * ============================================================================
 */

// ============================================================================
// 타입 정의
// ============================================================================

/** LLM 서비스 타입 */
export type LLMServiceType = 'chatgpt' | 'gemini' | 'perplexity' | 'grok' | 'claude'

/** LLM 서비스 상세 정보 인터페이스 */
export interface LLMServiceInfo {
  id: LLMServiceType
  name: string
  icon: string
  description: string
  details: string
  strength: string
  url: string
}

// ============================================================================
// LLM 서비스 정보 (레거시 llmCharacteristics + llmUrls 통합)
// ============================================================================

export const LLM_SERVICES: Record<LLMServiceType, LLMServiceInfo> = {
  chatgpt: {
    id: 'chatgpt',
    name: 'ChatGPT',
    icon: '🤖',
    description: 'SNS 후킹 분석',
    details: '후킹 효과성·SNS 최적화·행동 유도 분석',
    strength: '종합적 후킹 전략',
    url: 'https://chatgpt.com',
  },
  gemini: {
    id: 'gemini',
    name: 'Gemini',
    icon: '🧠',
    description: '심리적 후킹',
    details: '무의식 자극·감정 트리거·타겟 독자 분석',
    strength: '심리학적 접근',
    url: 'https://gemini.google.com',
  },
  perplexity: {
    id: 'perplexity',
    name: 'Perplexity',
    icon: '🔎',
    description: '트렌드 검증',
    details: 'SNS 트렌드·바이럴 가능성·신뢰성 강화',
    strength: '실시간 트렌드 분석',
    url: 'https://www.perplexity.ai',
  },
  grok: {
    id: 'grok',
    name: 'Grok',
    icon: '🚀',
    description: '임팩트 최적화',
    details: '강력한 후킹 문구·명확한 메시지·즉시 개선점',
    strength: '간결한 임팩트 분석',
    url: 'https://grok.com',
  },
  claude: {
    id: 'claude',
    name: 'Claude',
    icon: '🟣',
    description: '형식 엄수·긴 문맥',
    details: '형식 준수·안전성·장문 요약/구조화',
    strength: '정책/포맷 준수와 긴 문맥 처리',
    url: 'https://claude.ai/new',
  },
}

// ============================================================================
// LLM별 프롬프트 템플릿 (레거시 llmPrompts 1:1 포팅)
// ============================================================================

export const LLM_PROMPTS: Record<LLMServiceType, string> = {
  // --------------------------------------------------------------------------
  // ChatGPT 프롬프트: SNS 후킹 효과성·최적화·행동 유도
  // --------------------------------------------------------------------------
  chatgpt:
    `다음 글을 SNS 후킹 관점에서 분석해주세요. 특히 다음 요소들을 평가해주세요:

🎯 후킹 효과성:
- 첫 문장이 독자의 관심을 끌 수 있는가?
- 감정적 몰입을 유도하는가?
- 호기심을 자극하는 요소가 있는가?

📱 SNS 최적화:
- 읽기 쉬운 구조인가?
- 공유하고 싶은 욕구를 자극하는가?
- 댓글을 유도할 수 있는 요소가 있는가?

💡 개선 제안:
- 더 강력한 후킹 포인트 제안
- 감정적 반응을 높이는 방법
- 행동 유도(좋아요, 공유, 댓글) 강화 방안

📂 카테고리 추천:
- 이 글이 어떤 카테고리에 가장 적합한지 3가지 추천
- 각 카테고리의 적합도와 이유 설명
- 카테고리별 게시 전략 제안

[정책 준수 검사]
정책: '경제적 이익에 관한 현실성 없는 주장이나 약속(고수익 보장, 원금 보장, 무위험, 단기간 고수익, 확정 수익/퍼센트 보장 등)' 금지.
검토 대상 텍스트: 위 '분석할 글'
출력 형식(엄수):
위반 여부: [명백한 위반|위반 소지 높음|애매함(경고)|안전|명백한 비위반]
위반 위험 점수: [1|2|3|4|5]
위반 근거 문구: [...]
분석 사유: (핵심 근거를 3줄 이내로)

[2~3줄 카피 생성]
역할: 당신은 카피라이터입니다. 아래 '분석할 글'의 주제·정서·메시지를 유지하며 2~3줄 카피를 생성하세요.
요구사항:
- 정확히 2줄 또는 3줄만 출력(상황에 맞춰 선택). 줄바꿈으로 구분, 그 외 텍스트 금지.
- 2줄일 때: 1줄차=보편적·넓은 공감(원문과 일맥상통), 2줄차=구체·직접적·감정 이입 유발.
- 3줄일 때: 1줄차=보편적 메시지, 2줄차=맥락 전개(1줄과 연결), 3줄차=구체·직접적·감정 이입 유발.
- 간결·명확, 중복/과장/해시태그/이모지/따옴표/머리말·꼬리말 금지.

분석할 글:
`,

  // --------------------------------------------------------------------------
  // Gemini 프롬프트: 심리적 후킹·감정 트리거·타겟 독자
  // --------------------------------------------------------------------------
  gemini:
    `다음 글을 SNS 마케팅 전문가 관점에서 분석해주세요:

🧠 심리적 후킹 분석:
- 독자의 무의식을 자극하는 요소 분석
- 감정적 트리거 포인트 식별
- 인지 편향 활용도 평가

📊 타겟 독자 분석:
- 어떤 독자층에게 어필하는가?
- 공감대 형성 요소는 무엇인가?
- 행동 변화를 유도할 수 있는가?

🎨 표현력 개선:
- 더 강력한 표현으로 바꿀 부분
- 시각적 임팩트를 높이는 방법
- 기억에 남는 문구 만들기

📂 카테고리 추천:
- 이 글이 어떤 카테고리에 가장 적합한지 3가지 추천
- 각 카테고리의 적합도와 이유 설명
- 카테고리별 게시 전략 제안

[정책 준수 검사]
정책: '경제적 이익에 관한 현실성 없는 주장이나 약속(고수익 보장, 원금 보장, 무위험, 단기간 고수익, 확정 수익/퍼센트 보장 등)' 금지.
검토 대상 텍스트: 위 '분석할 글'
출력 형식(엄수):
위반 여부: [명백한 위반|위반 소지 높음|애매함(경고)|안전|명백한 비위반]
위반 위험 점수: [1|2|3|4|5]
위반 근거 문구: [...]
분석 사유: (핵심 근거를 3줄 이내로)

[2~3줄 카피 생성]
역할: 당신은 카피라이터입니다. 아래 '분석할 글'의 주제·정서·메시지를 유지하며 2~3줄 카피를 생성하세요.
요구사항:
- 정확히 2줄 또는 3줄만 출력(상황에 맞춰 선택). 줄바꿈으로 구분, 그 외 텍스트 금지.
- 2줄일 때: 1줄차=보편적·넓은 공감(원문과 일맥상통), 2줄차=구체·직접적·감정 이입 유발.
- 3줄일 때: 1줄차=보편적 메시지, 2줄차=맥락 전개(1줄과 연결), 3줄차=구체·직접적·감정 이입 유발.
- 간결·명확, 중복/과장/해시태그/이모지/따옴표/머리말·꼬리말 금지.

분석할 글:
`,

  // --------------------------------------------------------------------------
  // Perplexity 프롬프트: 트렌드 적합성·신뢰성·확산 가능성
  // --------------------------------------------------------------------------
  perplexity:
    `다음 글을 SNS 트렌드 및 신뢰성 관점에서 분석해주세요:

🔍 트렌드 적합성:
- 현재 SNS 트렌드와 부합하는가?
- 바이럴 가능성이 있는 주제인가?
- 시의적절한 타이밍인가?

📈 신뢰성 강화:
- 사실 확인이 필요한 부분
- 더 설득력 있는 근거 제시 방법
- 전문성 어필 요소 추가 방안

🌐 확산 가능성:
- 공유 가치가 있는 콘텐츠인가?
- 논란을 일으킬 수 있는 요소는?
- 긍정적 바이럴을 위한 개선점

📂 카테고리 추천:
- 이 글이 어떤 카테고리에 가장 적합한지 3가지 추천
- 각 카테고리의 적합도와 이유 설명
- 카테고리별 게시 전략 제안

[정책 준수 검사]
정책: '경제적 이익에 관한 현실성 없는 주장이나 약속(고수익 보장, 원금 보장, 무위험, 단기간 고수익, 확정 수익/퍼센트 보장 등)' 금지.
검토 대상 텍스트: 위 '분석할 글'
출력 형식(엄수):
위반 여부: [명백한 위반|위반 소지 높음|애매함(경고)|안전|명백한 비위반]
위반 위험 점수: [1|2|3|4|5]
위반 근거 문구: [...]
분석 사유: (핵심 근거를 3줄 이내로)

[2~3줄 카피 생성]
역할: 당신은 카피라이터입니다. 아래 '분석할 글'의 주제·정서·메시지를 유지하며 2~3줄 카피를 생성하세요.
요구사항:
- 정확히 2줄 또는 3줄만 출력(상황에 맞춰 선택). 줄바꿈으로 구분, 그 외 텍스트 금지.
- 2줄일 때: 1줄차=보편적·넓은 공감(원문과 일맥상통), 2줄차=구체·직접적·감정 이입 유발.
- 3줄일 때: 1줄차=보편적 메시지, 2줄차=맥락 전개(1줄과 연결), 3줄차=구체·직접적·감정 이입 유발.
- 간결·명확, 중복/과장/해시태그/이모지/따옴표/머리말·꼬리말 금지.

분석할 글:
`,

  // --------------------------------------------------------------------------
  // Grok 프롬프트: 임팩트 포인트·명확성·즉시 개선
  // --------------------------------------------------------------------------
  grok:
    `다음 글을 SNS 후킹 전문가 관점에서 간결하고 임팩트 있게 분석해주세요:

⚡ 임팩트 포인트:
- 가장 강력한 후킹 문장은?
- 독자에게 남을 핵심 메시지는?
- 행동을 유도하는 CTA는?

🎯 명확성 검증:
- 메시지가 명확하게 전달되는가?
- 불필요한 요소는 없는가?
- 핵심만 간결하게 전달하는가?

🚀 개선 액션:
- 즉시 적용 가능한 개선점
- 더 강력한 후킹 문구 제안
- 독자 반응을 높이는 방법

📂 카테고리 추천:
- 이 글이 어떤 카테고리에 가장 적합한지 3가지 추천
- 각 카테고리의 적합도와 이유 설명
- 카테고리별 게시 전략 제안

[정책 준수 검사]
정책: '경제적 이익에 관한 현실성 없는 주장이나 약속(고수익 보장, 원금 보장, 무위험, 단기간 고수익, 확정 수익/퍼센트 보장 등)' 금지.
검토 대상 텍스트: 위 '분석할 글'
출력 형식(엄수):
위반 여부: [명백한 위반|위반 소지 높음|애매함(경고)|안전|명백한 비위반]
위반 위험 점수: [1|2|3|4|5]
위반 근거 문구: [...]
분석 사유: (핵심 근거를 3줄 이내로)

[2~3줄 카피 생성]
역할: 당신은 카피라이터입니다. 아래 '분석할 글'의 주제·정서·메시지를 유지하며 2~3줄 카피를 생성하세요.
요구사항:
- 정확히 2줄 또는 3줄만 출력(상황에 맞춰 선택). 줄바꿈으로 구분, 그 외 텍스트 금지.
- 2줄일 때: 1줄차=보편적·넓은 공감(원문과 일맥상통), 2줄차=구체·직접적·감정 이입 유발.
- 3줄일 때: 1줄차=보편적 메시지, 2줄차=맥락 전개(1줄과 연결), 3줄차=구체·직접적·감정 이입 유발.
- 간결·명확, 중복/과장/해시태그/이모지/따옴표/머리말·꼬리말 금지.

분석할 글:
`,

  // --------------------------------------------------------------------------
  // Claude 프롬프트: 구조적 분석·형식 준수·안전성
  // --------------------------------------------------------------------------
  claude:
    `다음 글을 포맷 엄수와 긴 문맥 이해에 강한 전문가로서 분석해주세요:

📌 구조적 분석:
- 주제·메시지·타겟 요약(1~2줄)
- 논리 흐름과 결론의 일치 여부

🧭 형식 준수 점검:
- 요구된 출력 형식/톤 준수 여부
- 모호/과장/과도한 확언 존재 여부

💡 개선 제안:
- 형식/명확성/근거 보강 포인트
- 안전한 대안 표현(과장 최소화)

[정책 준수 검사]
정책: '경제적 이익에 관한 현실성 없는 주장이나 약속(고수익 보장, 원금 보장, 무위험, 단기간 고수익, 확정 수익/퍼센트 보장 등)' 금지.
검토 대상 텍스트: 위 '분석할 글'
출력 형식(엄수):
위반 여부: [명백한 위반|위반 소지 높음|애매함(경고)|안전|명백한 비위반]
위반 위험 점수: [1|2|3|4|5]
위반 근거 문구: [...]
분석 사유: (핵심 근거를 3줄 이내로)

[2~3줄 카피 생성]
역할: 당신은 카피라이터입니다. 아래 '분석할 글'의 주제·정서·메시지를 유지하며 2~3줄 카피를 생성하세요.
요구사항:
- 정확히 2줄 또는 3줄만 출력(상황에 맞춰 선택). 줄바꿈으로 구분, 그 외 텍스트 금지.
- 2줄일 때: 1줄차=보편적·넓은 공감(원문과 일맥상통), 2줄차=구체·직접적·감정 이입 유발.
- 3줄일 때: 1줄차=보편적 메시지, 2줄차=맥락 전개(1줄과 연결), 3줄차=구체·직접적·감정 이입 유발.
- 간결·명확, 중복/과장/해시태그/이모지/따옴표/머리말·꼬리말 금지.

분석할 글:
`,
}
